import {Button, openConfirmDialog, showNotification, Tooltip} from 'platform/components';
import {HStack} from 'platform/foundation';

// eslint-disable-next-line no-restricted-imports -- Intended
import {path} from 'ramda';
import {isNotNilOrEmpty, isPlainObj} from 'ramda-adjunct';

import {useGeneratePlatformDescriptionMutation} from '@dms/api/sales';
import {useGetPlatformDescriptionGeneratorConfigQuery} from '@dms/api/settings';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';
import {useFormRenderer} from '@dms/teas';

import {suffixTestId, TestIdProps} from 'shared/web';

interface PlatformDescriptionFieldActionsProps extends TestIdProps {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  platformName: string;
  isDisabled: boolean;
  fieldName: string;
}

interface DescriptionAttributeInForm {
  value?: unknown;
  override?: unknown;
}

const isDescriptionAttributeInForm = (
  attribute: unknown
): attribute is DescriptionAttributeInForm => isPlainObj(attribute);

export function PlatformDescriptionFieldActions(props: PlatformDescriptionFieldActionsProps) {
  const {form} = useFormRenderer();

  const [canGenerateDescription] = usePermissions({
    permissionKeys: ['vehicleSalesGenerateDescription'],
  });

  const {data: aiConfig, isLoading: isAiConfigLoading} =
    useGetPlatformDescriptionGeneratorConfigQuery(
      {platformCode: props.platformCode},
      {skip: !canGenerateDescription}
    );

  const [generateDescription, {isLoading: isGenerating}] = useGeneratePlatformDescriptionMutation();

  if (!canGenerateDescription) {
    return null;
  }

  const isAiEnabledForPlatform = aiConfig?.overrides.enabled ?? aiConfig?.global.enabled ?? false;
  const generateTooltip = !isAiEnabledForPlatform
    ? i18n.t('page.advertisement.labels.aiDisabledForPlatformTooltip')
    : undefined;

  // Read the description field as the user currently sees it in the form.
  // We confirm on Generate based on this — not on the persisted backend state —
  // so unsaved manual edits still trigger the "replace?" prompt.
  const getCurrentDescriptionInForm = () => {
    const segments = props.fieldName.split(/\.|\[|\]/).filter(Boolean);
    const attribute = path<unknown>(segments, form.getState().values);
    return isDescriptionAttributeInForm(attribute)
      ? {
          value: typeof attribute.value === 'string' ? attribute.value : null,
          override: attribute.override === true,
        }
      : {value: null, override: false};
  };

  const performGenerate = () => {
    generateDescription({
      vehicleId: props.vehicleId,
      platformCode: props.platformCode,
    })
      .unwrap()
      .then((response) => {
        form.change(`${props.fieldName}.value`, response.content);
        form.change(`${props.fieldName}.override`, true);
        showNotification.success(
          i18n.t('page.advertisement.notifications.platformDescriptionGenerated', {
            platformName: props.platformName,
          })
        );
      })
      .catch(handleApiError);
  };

  const handleGenerate = () => {
    const current = getCurrentDescriptionInForm();
    if (current.override && isNotNilOrEmpty(current.value)) {
      openConfirmDialog({
        text: i18n.t('page.advertisement.labels.platformGenerateReplaceConfirm'),
        onConfirm: performGenerate,
      });
      return;
    }
    performGenerate();
  };

  return (
    <HStack spacing={2} align="center" justify="flex-end">
      <Tooltip label={generateTooltip} isDisabled={!generateTooltip}>
        <Button
          variant="link"
          leftIcon="action/ai"
          isDisabled={
            !isAiEnabledForPlatform || isGenerating || isAiConfigLoading || props.isDisabled
          }
          onClick={handleGenerate}
          isLoading={isGenerating}
          title={i18n.t('page.advertisement.actions.generateForPlatform', {
            platformName: props.platformName,
          })}
          data-testid={suffixTestId('generateDescription', props)}
        />
      </Tooltip>
    </HStack>
  );
}
